import { ExportDuck } from './ExportDuck.typed'
import { EXPORT_ACTIONS } from './ExportDuck.actions'
import { debounce, put, select, takeEvery, call } from 'redux-saga/effects'
import { ErrorDuck } from '@aytm/app/insights/dashboard/duckness/ducks/ErrorDuck'
import { DataDuck } from '@aytm/app/insights/dashboard/duckness/ducks/DataDuck'
import { ERROR_ACTIONS } from '@aytm/app/insights/dashboard/duckness/ducks/ErrorDuck/ErrorDuck.actions'
import { getRequest } from '@aytm/app/insights/dashboard/helpers/request'
import statQuerySaga from '../DataDuck/sagas/statQuery.saga'
import { asyncTimeout } from '@aytm/app/insights/dashboard/lib/promises'
import staticStore from '@aytm/app/insights/dashboard/stores/static.store'
import { saveCurrentSchema } from '@aytm/app/insights/dashboard/helpers/requests'
import { safelyExecuteSaga, safelyExecuteSync } from '@aytm/app/insights/dashboard/lib/safety'

const REQUEST_DEBOUNCE_TIMEOUT = 300
const REQUEST_REFETCH_TIMEOUT = 1000

ExportDuck.saga(function* run(duckFace) {
  yield debounce(
    REQUEST_DEBOUNCE_TIMEOUT,
    [duckFace.action[EXPORT_ACTIONS.EXPORT_START].actionType],
    function* runDebounced(action) {
      yield put(ErrorDuck.action[ERROR_ACTIONS.CLEAR_ERRORS]({ types: ['export'] }))
      const state = yield select(state => state)
      const { params } = action.payload
      try {
        const request = getRequest({ ...params, type: 'export' }, state)
        yield put(duckFace.action[EXPORT_ACTIONS.EXPORT_REQUEST]({ request }))
      } catch (error) {
        // debugger
        yield put(duckFace.action[EXPORT_ACTIONS.EXPORT_ERROR]({ params, message: 'Export failed', reason: error }))
      }
    }
  )
})

//
ExportDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[EXPORT_ACTIONS.EXPORT_REQUEST].actionType], function* runEvery(action) {
    const { request } = action.payload
    // debugger
    const accessKey = yield select(DataDuck.select.accessKey)
    /**
     * @type {import('@aytm/app/insights/dashboard/models/types.ServerResponse').ServerResponse.InsightsExport.RootType}
     */
    const [error, response] = yield call(safelyExecuteSaga, statQuerySaga, request, accessKey)
    if (error) {
      // Phasing out from using EXPORT_ERROR
      yield put(duckFace.action[EXPORT_ACTIONS.EXPORT_ERROR]())
      yield put(
        ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({
          type: 'export',
          error: 'Error fetching export data.' + (error.message ? ' Reason: ' + error.message : '')
        })
      )
      return
    }
    let { status, blockUID, bodyText, queryHash, exportResult, progress } = response
    while ('WAIT' === status) {
      yield put(duckFace.action[EXPORT_ACTIONS.EXPORT_PROGRESS]({ progress, blockUID }))
      yield call(asyncTimeout, REQUEST_REFETCH_TIMEOUT)
      Object.assign(request, { queryHash, query: undefined })
      /**
       * @type {[Error, import('@aytm/app/insights/dashboard/models/types.ServerResponse').ServerResponse.InsightsExport.RootType]}
       */
      const [error, newResponse] = yield call(safelyExecuteSaga, statQuerySaga, request, accessKey)
      if (error) {
        yield put(
          ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({
            type: 'export',
            error: 'Error fetching export data.' + (error.message ? ' Reason: ' + error.message : '')
          })
        )
        return
      }
      ;({ status, blockUID, bodyText, queryHash, exportResult, progress } = newResponse)
    }
    if ('OK' === status) {
      yield put(duckFace.action[EXPORT_ACTIONS.EXPORT_SUCCESS]({ blockUID, exportResult }))
    } else {
      const error = String(bodyText)
      yield put(duckFace.action[EXPORT_ACTIONS.EXPORT_ERROR]({ error }))
    }
  })
})

ExportDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[EXPORT_ACTIONS.EXPORT_SUCCESS].actionType], function* runEvery(action) {
    const {
      contentType,
      file: { uri: fileUrl, name: fileName }
    } = action.payload.exportResult
    const [error, _success] = yield call(safelyExecuteSaga, downloadFile, fileUrl, fileName, contentType)
    yield error
      ? put(
          ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({
            type: 'export',
            error: 'Download failed' + (error.message ? ' Reason: ' + error.message : '')
          })
        )
      : put(duckFace.action[EXPORT_ACTIONS.EXPORT_SUCCESS_CLEANUP]())
  })
})

ExportDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[EXPORT_ACTIONS.EXPORT_UPDATE_DIALOG].actionType], function* runEvery(action) {
    yield call(saveCurrentSchema, action)
  })
})

export default ExportDuck

/**
 *
 * @param {string} fileUrl
 * @param {string} fileName
 * @param {string} [contentType]
 * @returns {Promise<void>}
 */
async function downloadFile(fileUrl, fileName, _contentType) {
  const href = safelyExecuteSync(
    () => new URL(fileUrl),
    () => safelyExecuteSync(() => new URL(`${staticStore.urls.file}/${fileUrl}`), null)
  )
  if (!href) {
    throw new Error('Invalid file URL')
  }

  const anchor = document.createElement('a')
  anchor.href = href
  anchor.setAttribute('download', fileName)
  anchor.click()
}
